import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { BrandMemoDTO } from '@magig/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import { type Brand, flagMemoReceiptMistake } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { formatDate } from '../lib/format.js';
import { brandMemoByIdQuery, brandsQuery, meQuery } from '../lib/queries.js';

/**
 * Single memo detail — `/brand/$slug/memos/$memoId`. Renders every
 * section the memo agent authors: headline, reading blocks, numbers
 * + verdict, receipts, asks, per-campaign theses, trust check.
 *
 * Memos are frozen at publish so this page is mostly a long-form
 * read — no mutations, just navigation back to the archive.
 */
export const Route = createFileRoute('/brand/$slug/memos/$memoId')({
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    await queryClient.ensureQueryData(brandMemoByIdQuery(brand.id, params.memoId));
    return { brand };
  },
  component: MemoDetailPage,
});

function MemoDetailPage() {
  const { brand } = Route.useRouteContext();
  const { memoId } = Route.useParams();
  useBrandTitle(brand.slug, ['memos', memoId.slice(0, 8)]);
  const { data: memo } = useQuery(brandMemoByIdQuery(brand.id, memoId));

  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active="home" />
      <main className="mx-auto max-w-[760px] px-4 py-8 sm:px-6 sm:py-10">
        <BackLink brand={brand} />
        {memo ? <MemoBody memo={memo} brand={brand} /> : <NotFound brand={brand} />}
      </main>
    </div>
  );
}

function BackLink({ brand }: { brand: Brand }) {
  return (
    <Link
      to="/brand/$slug/memos"
      params={{ slug: brand.slug }}
      className="mb-4 inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55 underline-offset-2 hover:text-[var(--ink)] hover:underline"
    >
      {'←'} <Trans>all memos</Trans>
    </Link>
  );
}

function NotFound({ brand }: { brand: Brand }) {
  return (
    <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--pulse)]">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--pulse)]">
        {'↘'} <Trans>memo not found</Trans>
      </p>
      <h3 className="mt-1 font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        <Trans>i can't find that one.</Trans>
      </h3>
      <p className="mt-2 text-[13px] text-[var(--ink)]/65">
        <Trans>
          the memo id doesn't match anything on {brand.name}. it may have been deleted, or you're
          signed in on the wrong brand.
        </Trans>
      </p>
    </div>
  );
}

function MemoBody({ memo, brand }: { memo: BrandMemoDTO; brand: Brand }) {
  return (
    <article className="flex flex-col gap-5">
      <Greeting memo={memo} />
      {memo.reading.map((block) => (
        <ReadingSection key={`${block.kind}-${block.heading}`} block={block} />
      ))}
      <NumbersSection memo={memo} />
      {memo.receipts.length > 0 ? <ReceiptsSection memo={memo} brand={brand} /> : null}
      <AsksSection memo={memo} />
      {memo.thesisByCampaign.length > 0 ? <ThesisSection memo={memo} /> : null}
      <TrustCheckSection memo={memo} />
    </article>
  );
}

function Greeting({ memo }: { memo: BrandMemoDTO }) {
  return (
    <header className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--spell)] sm:p-7">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        <Trans>↘ memo · {formatPeriodLabel(memo.periodStart)}</Trans>
      </p>
      <h1 className="mt-2 font-display text-[26px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink)]">
        {memo.headline}
      </h1>
    </header>
  );
}

function ReadingSection({ block }: { block: BrandMemoDTO['reading'][number] }) {
  const tone = READING_TONES[block.kind] ?? {
    bg: 'bg-white',
    accent: 'text-[var(--plum)]',
  };
  return (
    <section
      className={`rounded-2xl border-[1.5px] border-[var(--ink)] p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6 ${tone.bg}`}
    >
      <p
        className={`font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] ${tone.accent}`}
      >
        ↘ {block.heading}
      </p>
      <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-[var(--ink)]/85">
        {block.body}
      </p>
    </section>
  );
}

const READING_TONES: Record<string, { bg: string; accent: string }> = {
  how_im_reading: { bg: 'bg-white', accent: 'text-[var(--plum)]' },
  whats_working: { bg: 'bg-amber-50', accent: 'text-amber-900' },
  whats_not_working: { bg: 'bg-rose-50', accent: 'text-rose-800' },
  what_i_got_wrong: { bg: 'bg-orange-50', accent: 'text-orange-900' },
  whats_changing: { bg: 'bg-white', accent: 'text-[var(--plum)]' },
};

function NumbersSection({ memo }: { memo: BrandMemoDTO }) {
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>where the numbers are</Trans>
      </p>
      {memo.numbers.metrics.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {memo.numbers.metrics.map((m) => (
            <div
              key={m.label}
              className="rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-3"
            >
              <p className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
                {m.label.toLowerCase()}
              </p>
              <p className="mt-1 font-display text-[18px] font-bold text-[var(--ink)]">
                {m.currentDisplay}
              </p>
              {m.deltaPercent !== null ? (
                <p
                  className={`mt-0.5 font-mono text-[10px] ${m.deltaPercent >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}
                >
                  {m.deltaPercent >= 0 ? '↑' : '↓'} {Math.abs(m.deltaPercent).toFixed(0)}%
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-[12.5px] text-[var(--ink)]/55">
          <Trans>no paid metrics this week — still pre-data.</Trans>
        </p>
      )}
      <p className="mt-3 text-[13px] italic text-[var(--ink)]/75">
        <Trans>verdict: {memo.numbers.verdict.toLowerCase()}</Trans>
      </p>
    </section>
  );
}

function ReceiptsSection({ memo, brand }: { memo: BrandMemoDTO; brand: Brand }) {
  const { t } = useLingui();

  const queryClient = useQueryClient();
  const [flaggingIdx, setFlaggingIdx] = useState<number | null>(null);
  const flag = useMutation({
    mutationFn: (receiptIdx: number) => flagMemoReceiptMistake(brand.id, memo.id, receiptIdx),
    onSuccess: async (res) => {
      magToast.success(
        i18n._(msg`flagged.`),
        i18n._(msg`dropped ${res.decisionType.replace(/_/g, ' ')} to ${res.newLevel}.`),
      );
      setFlaggingIdx(null);
      await queryClient.invalidateQueries({
        queryKey: ['brand-memo', 'by-id', brand.id, memo.id],
      });
      await queryClient.invalidateQueries({ queryKey: ['brand-memo', 'latest', brand.id] });
    },
    onError: (err) => magToast.error("couldn't flag that.", founderErrorCopy(err, String(err))),
  });
  const flaggingReceipt = flaggingIdx !== null ? memo.receipts[flaggingIdx] : null;
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>what i shipped</Trans>
      </p>
      <ul className="mt-3 flex flex-col gap-1.5">
        {memo.receipts.map((r, idx) => {
          const flaggable = decisionTypeFromReceiptKind(r.kind) !== null;
          return (
            <li
              key={`${r.kind}-${r.summary}`}
              className="group flex items-baseline gap-2 text-[13px] text-[var(--ink)]/85"
            >
              <span
                className={`mt-0.5 inline-block rounded-full px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.06em] ${receiptChip(r.kind)}`}
              >
                {receiptLabel(r.kind)}
              </span>
              <span className="flex-1">{r.summary}</span>
              {flaggable ? (
                <button
                  type="button"
                  disabled={flag.isPending}
                  onClick={() => setFlaggingIdx(idx)}
                  className="font-mono text-[9.5px] uppercase tracking-[0.08em] text-[var(--ink)]/35 underline-offset-2 transition hover:text-[var(--pulse)] hover:underline disabled:opacity-50"
                  title={t`flag this receipt as a mistake`}
                >
                  {'↘'} <Trans>flag</Trans>
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={flaggingIdx !== null}
        title={t`mark this as wrong?`}
        body={
          flaggingReceipt ? (
            <Trans>
              i'll drop my authority on the <b>{flaggingReceipt.kind.replace(/_/g, ' ')}</b> lane
              one level. you'll re-approve future actions of this kind until i earn it back.
            </Trans>
          ) : null
        }
        confirmLabel={t`flag it`}
        busyLabel={t`flagging…`}
        destructive
        busy={flag.isPending}
        onConfirm={() => {
          if (flaggingIdx !== null) flag.mutate(flaggingIdx);
        }}
        onCancel={() => setFlaggingIdx(null)}
      />
    </section>
  );
}

/** Mirror of the server-side mapping in apps/api/src/brand-memo.ts.
 *  Kept in sync by inspection; receipt kinds with no lane don't get
 *  the flag affordance rendered. */
function decisionTypeFromReceiptKind(kind: string): string | null {
  switch (kind) {
    case 'meta_paid_ad_launched':
    case 'meta_paid_ad_paused':
      return 'meta_paid_publish';
    case 'google_search_ad_launched':
    case 'google_search_ad_paused':
      return 'google_paid_publish';
    case 'instagram_post_published':
      return 'meta_organic_publish';
    case 'brain_evolution_applied':
      return 'brain_edit';
    case 'budget_reallocated':
      return 'cross_channel_budget';
    default:
      return null;
  }
}

function receiptChip(kind: string): string {
  switch (kind) {
    case 'meta_paid_ad_launched':
    case 'meta_paid_ad_paused':
      return 'bg-blue-50 text-blue-900';
    case 'google_search_ad_launched':
    case 'google_search_ad_paused':
      return 'bg-emerald-50 text-emerald-900';
    case 'instagram_post_published':
      return 'bg-pink-50 text-pink-900';
    case 'creative_drop_published':
    case 'brain_evolution_applied':
    case 'budget_reallocated':
      return 'bg-[var(--paper)] text-[var(--plum)]';
    default:
      return 'bg-zinc-100 text-zinc-700';
  }
}

function receiptLabel(kind: string): string {
  switch (kind) {
    case 'meta_paid_ad_launched':
      return i18n._(msg`meta · launched`);
    case 'meta_paid_ad_paused':
      return i18n._(msg`meta · paused`);
    case 'google_search_ad_launched':
      return i18n._(msg`google · launched`);
    case 'google_search_ad_paused':
      return i18n._(msg`google · paused`);
    case 'instagram_post_published':
      return i18n._(msg`ig · posted`);
    case 'creative_drop_published':
      return i18n._(msg`drop`);
    case 'brain_evolution_applied':
      return i18n._(msg`brain`);
    case 'budget_reallocated':
      return i18n._(msg`budget`);
    default:
      return kind.replace(/_/g, ' ');
  }
}

function AsksSection({ memo }: { memo: BrandMemoDTO }) {
  const hero = memo.asks.hero;
  const batched = memo.asks.batched;
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>what i need from you</Trans>
      </p>
      {hero ? (
        <div className="mt-3 rounded-xl border-[1.5px] border-[var(--ink)] bg-[var(--spell)]/30 p-3">
          <p className="font-display text-[15px] font-semibold text-[var(--ink)]">{hero.title}</p>
        </div>
      ) : (
        <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-emerald-700">
          {'✓'} <Trans>you're clear — nothing needing your tap this week.</Trans>
        </p>
      )}
      {batched.length > 0 ? (
        <div className="mt-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
            {'↘'} <Trans>smaller stuff</Trans>
          </p>
          <ul className="mt-1 flex flex-col gap-1">
            {batched.map((a) => (
              <li key={a.title} className="text-[13px] text-[var(--ink)]/80">
                · {a.title}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function ThesisSection({ memo }: { memo: BrandMemoDTO }) {
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>what i'm testing next</Trans>
      </p>
      <ul className="mt-3 flex flex-col gap-3">
        {memo.thesisByCampaign.map((t) => (
          <li
            key={t.campaignId}
            className="rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-3"
          >
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
              {t.campaignName.toLowerCase()}
            </p>
            <p className="mt-1 text-[13px] text-[var(--ink)]/85">{t.hypothesis}</p>
            {t.signalToWatch ? (
              <p className="mt-1 text-[12px] italic text-[var(--ink)]/65">
                <Trans>signal: {t.signalToWatch}</Trans>
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function TrustCheckSection({ memo }: { memo: BrandMemoDTO }) {
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>trust check</Trans>
      </p>
      <p className="mt-2 text-[13px] text-[var(--ink)]/85">{memo.trustCheck.summary}</p>
      {memo.trustCheck.drops.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1.5">
          {memo.trustCheck.drops.map((d) => (
            <li
              key={`${d.decisionType}-${d.reason}`}
              className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-[12.5px] text-rose-900"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.06em]">
                {d.decisionType}
              </span>{' '}
              · {d.from} → {d.to} · {d.reason}
            </li>
          ))}
        </ul>
      ) : null}
      {memo.trustCheck.monthlyAutonomousBudgetUsd !== undefined ? (
        <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <Trans>autonomous spend: ${memo.trustCheck.autonomousSpendUsd.toFixed(0)}</Trans>
          {memo.trustCheck.monthlyAutonomousBudgetUsd ? (
            <Trans> / ${memo.trustCheck.monthlyAutonomousBudgetUsd.toFixed(0)} cap</Trans>
          ) : null}
        </p>
      ) : null}
      {memo.trustCheck.spendByLane && memo.trustCheck.spendByLane.length > 0 ? (
        <div className="mt-3">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
            {'↘'} <Trans>lane split</Trans>
          </p>
          <ul className="mt-1.5 flex flex-col gap-1">
            {memo.trustCheck.spendByLane.map((lane) => {
              // 'always_on' and 'campaign' are OUR lane vocabulary rendered as
              // prose, so they get translated; the campaign's own name does not.
              const label =
                lane.lane === 'always_on'
                  ? i18n._(msg`always-on`)
                  : (lane.campaignName?.toLowerCase() ?? i18n._(msg`campaign`));
              const key = lane.lane === 'always_on' ? 'always_on' : (lane.campaignId ?? label);
              return (
                <li
                  key={key}
                  className="grid grid-cols-[1fr_auto] items-baseline gap-2 font-mono text-[10.5px] uppercase tracking-[0.06em] text-[var(--ink)]/65"
                >
                  <span className="truncate">{label}</span>
                  <span>
                    ${lane.spentUsd.toFixed(0)}
                    {lane.budgetUsd ? <Trans> / ${lane.budgetUsd.toFixed(0)} cap</Trans> : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function formatPeriodLabel(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    const fmt = formatDate(d, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return `week of ${fmt.toLowerCase()}`;
  } catch {
    return `week of ${iso}`;
  }
}
